import { useState } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useBeheerder } from '../hooks/useBeheerder';
import { useBranding } from '../contexts/BrandingContext';
import { useOrgInfo } from '../contexts/OrgInfoContext';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import { XxllncAiLogo, McpIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';

function NavLink({ label, active, onClick, textColor }: {
  label: string; active: boolean; onClick: () => void; textColor?: string;
}) {
  const hasCustomColor = !!textColor;
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        px: 1.5,
        py: 0.75,
        borderRadius: 1, // use MUI spacing unit
        fontSize: '0.875rem',
        fontWeight: active ? 600 : 500,
        color: textColor
          ? (active ? textColor : textColor)
          : (active ? 'text.primary' : 'text.secondary'),
        opacity: active ? 1 : (hasCustomColor ? 0.7 : 1),
        bgcolor: active
          ? (hasCustomColor ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)')
          : 'transparent',
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: hasCustomColor ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)',
          opacity: 1,
        },
      }}
    >
      {label}
    </Box>
  );
}

export default function Layout() {
  const { user, logout } = useOidcContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isBeheerder = useBeheerder();
  const orgInfo = useOrgInfo();
  const branding = useBranding();
  const t = useTranslation();

  // Remount the routed page (and so re-run its data-loading effects) without a
  // full browser reload. There is no shared query cache in the portal, so
  // bumping a key on the Outlet wrapper is the generic equivalent of the
  // react-admin dashboard's refresh button.
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
    setSpinning(true);
    window.setTimeout(() => setSpinning(false), 600);
  };

  const displayName = (user?.name || user?.email || '?') as string;
  const initials = displayName
    .split(' ')
    .map((w: string) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const portalTitle = branding.portal_title || 'MCP Hub';
  const headerBg = branding.header_color || '#fff';
  const headerTextColor = branding.header_text_color || undefined;
  const headerBorder = branding.header_color
    ? 'none'
    : '1px solid rgba(0,0,0,0.07)';
  const hasLogo = !!(branding.logo_url && branding.logo_url.trim());
  const isBranded = !!(branding.portal_title || branding.primary_color);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: headerBg,
          borderBottom: headerBorder,
          boxShadow: branding.header_color ? '0 1px 8px rgba(0,0,0,0.15)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ gap: 1, minHeight: '60px !important' }}>
            {/* Logo section */}
            <Box
              onClick={() => navigate('/')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mr: 1,
                py: 1,
              }}
            >
              {hasLogo ? (
                <Box
                  component="img"
                  src={branding.logo_url!}
                  alt="logo"
                  sx={{ maxHeight: '2rem', maxWidth: 160, width: 'auto' }}
                />
              ) : !isBranded ? (
                <XxllncAiLogo height={26} />
              ) : null}
            </Box>

            {/* Divider - only show when logo is present */}
            {hasLogo && (
              <Box sx={{
                width: '1px',
                height: 24,
                bgcolor: headerTextColor ? `${headerTextColor}33` : 'rgba(0,0,0,0.1)',
                mx: 1,
              }} />
            )}

            {/* App name */}
            <Box
              onClick={() => navigate('/')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mr: 3,
              }}
            >
              <McpIcon sx={{ fontSize: 18, color: headerTextColor || 'text.primary' }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  color: headerTextColor || 'text.primary',
                  letterSpacing: '-0.01em',
                }}
              >
                {portalTitle}
              </Typography>
            </Box>

            {/* Navigation */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <NavLink
                label={t('nav.servers')}

                active={location.pathname === '/'}
                onClick={() => navigate('/')}
                textColor={headerTextColor}
              />
              {isBeheerder && (
                <NavLink
                  label={t('nav.workflows')}

                  active={location.pathname.startsWith('/workflows')}
                  onClick={() => navigate('/workflows')}
                  textColor={headerTextColor}
                />
              )}
              {isBeheerder && (
                <NavLink
                  label={t('nav.myRequests')}
  
                  active={location.pathname === '/my-requests'}
                  onClick={() => navigate('/my-requests')}
                  textColor={headerTextColor}
                />
              )}
              {isBeheerder && (
                <NavLink
                  label={t('nav.users')}
  
                  active={location.pathname === '/seats'}
                  onClick={() => navigate('/seats')}
                  textColor={headerTextColor}
                />
              )}
              {isBeheerder && orgInfo?.hub_tier !== 'free' && (
                <NavLink
                  label={t('nav.usage')}

                  active={location.pathname === '/usage'}
                  onClick={() => navigate('/usage')}
                  textColor={headerTextColor}
                />
              )}
              <NavLink
                label={t('nav.help')}
                active={location.pathname === '/help'}
                onClick={() => navigate('/help')}
                textColor={headerTextColor}
              />
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* User */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title={t('common.refreshTooltip')}>
                <IconButton
                  onClick={handleRefresh}
                  size="small"
                  sx={{
                    'color': headerTextColor || 'text.disabled',
                    '@keyframes xxllncSpin': { to: { transform: 'rotate(360deg)' } },
                    '& .MuiSvgIcon-root': spinning
                      ? { animation: 'xxllncSpin 0.6s ease-in-out' }
                      : {},
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={user?.picture}
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                  }}
                >
                  {initials}
                </Avatar>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: headerTextColor || 'text.secondary',
                    fontWeight: 500,
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    opacity: headerTextColor ? 0.8 : 1,
                  }}
                >
                  {user?.name || user?.email}
                </Typography>
              </Box>
              <Tooltip title={t('common.logoutTooltip')}>
                <IconButton
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                  size="small"
                  sx={{ color: headerTextColor || 'text.disabled' }}
                >
                  <LogoutIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box sx={{ flexGrow: 1 }}>
        <Container maxWidth="lg" sx={{ py: 5 }} key={refreshKey}>
          <Outlet />
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.5,
          borderTop: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        {hasLogo ? (
          <Box
            component="img"
            src={branding.logo_url!}
            alt="logo"
            sx={{ maxHeight: '1.2rem', maxWidth: 100, width: 'auto', opacity: 0.3 }}
          />
        ) : !isBranded ? (
          <XxllncAiLogo height={16} style={{ opacity: 0.35 }} />
        ) : (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 500 }}>
            {portalTitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
